import { Platform } from 'react-native';
import type { DatadogProviderConfiguration } from 'expo-datadog';
import { config as appConfig } from '@/config/env';

type RumModule = {
  DdRum?: {
    addAction?: (type: string, name: string, attributes?: Record<string, unknown>) => void;
    addError?: (
      message: string,
      source: string,
      stacktrace: string,
      attributes?: Record<string, unknown>,
    ) => void;
    startView?: (
      key: string,
      name: string,
      attributes?: Record<string, unknown>,
    ) => Promise<void> | void;
    stopView?: (key: string, attributes?: Record<string, unknown>) => Promise<void> | void;
  };
  RumActionType?: {
    CUSTOM?: string;
  };
  ErrorSource?: {
    SOURCE?: string;
  };
  BatchSize?: Record<string, unknown>;
  DatadogProviderConfiguration?: new (
    clientToken: string,
    env: string,
    applicationId: string,
    trackInteractions: boolean,
    trackResources: boolean,
    trackErrors: boolean,
  ) => DatadogProviderConfiguration;
  SdkVerbosity?: Record<string, unknown>;
  UploadFrequency?: Record<string, unknown>;
};

type BrowserRumModule = {
  datadogRum?: {
    init?: (configuration: Record<string, unknown>) => void;
    addAction?: (name: string, context?: Record<string, unknown>) => void;
    addError?: (error: Error, context?: Record<string, unknown>) => void;
    startView?: (view: { name: string; context?: Record<string, unknown> }) => void;
  };
};

type BrowserLogsModule = {
  datadogLogs?: {
    init?: (configuration: Record<string, unknown>) => void;
    logger?: {
      debug?: (message: string, context?: Record<string, unknown>) => void;
      info?: (message: string, context?: Record<string, unknown>) => void;
      warn?: (message: string, context?: Record<string, unknown>) => void;
      error?: (message: string, context?: Record<string, unknown>) => void;
    };
  };
};

type NativeLogsModule = {
  DdLogs?: {
    debug?: (message: string, context?: Record<string, unknown>) => void;
    info?: (message: string, context?: Record<string, unknown>) => void;
    warn?: (message: string, context?: Record<string, unknown>) => void;
    error?: (message: string, context?: Record<string, unknown>) => void;
  };
};

type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error';

const defaultConsole = {
  debug: console.debug.bind(console),
  error: console.error.bind(console),
  log: console.log.bind(console),
  warn: console.warn.bind(console),
};

let browserRumInitialized = false;
let browserLogsInitialized = false;
let clientConsoleForwardingInstalled = false;
let emittingClientLog = false;
let nativeLogsReady = Platform.OS === 'web';

const SENSITIVE_KEY_PATTERN = /(token|password|secret|authorization|cookie|api[-_]?key|session|credential)/i;

const getRumModule = (): RumModule | null => {
  if (Platform.OS === 'web') {
    return null;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('expo-datadog') as RumModule;
  } catch {
    return null;
  }
};

const getBrowserRum = (): BrowserRumModule['datadogRum'] | null => {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const browserRumModule = require('@datadog/browser-rum') as BrowserRumModule;
    return browserRumModule.datadogRum ?? null;
  } catch {
    return null;
  }
};

const getBrowserLogs = (): BrowserLogsModule['datadogLogs'] | null => {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const browserLogsModule = require('@datadog/browser-logs') as BrowserLogsModule;
    return browserLogsModule.datadogLogs ?? null;
  } catch {
    return null;
  }
};

const getNativeLogsModule = (): NativeLogsModule | null => {
  if (Platform.OS === 'web') {
    return null;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('expo-datadog') as NativeLogsModule;
  } catch {
    return null;
  }
};

const sanitizeLogValue = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack,
    };
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value === null || value === undefined) {
    return value;
  }
  try {
    const parsed = JSON.parse(JSON.stringify(value));
    if (Array.isArray(parsed)) {
      return parsed.map((entry) => sanitizeLogValue(entry));
    }
    if (parsed && typeof parsed === 'object') {
      return Object.fromEntries(
        Object.entries(parsed as Record<string, unknown>).map(([key, entry]) => [
          key,
          SENSITIVE_KEY_PATTERN.test(key) ? '[REDACTED]' : sanitizeLogValue(entry),
        ]),
      );
    }
    return parsed;
  } catch {
    return String(value);
  }
};

const logMessageFromArgs = (args: unknown[]): string => {
  const firstArg = args[0];
  if (typeof firstArg === 'string') {
    return firstArg;
  }
  if (firstArg instanceof Error) {
    return `${firstArg.name}: ${firstArg.message}`;
  }
  if (firstArg === null || firstArg === undefined) {
    return String(firstArg ?? '[console]');
  }
  return '[console event]';
};

const logContextFromArgs = (args: unknown[]): Record<string, unknown> | undefined => {
  if (args.length <= 1) {
    return undefined;
  }
  const details = args.slice(1).map((arg) => sanitizeLogValue(arg));
  return { details };
};

const asError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
};

const shouldForwardConsoleLevel = (level: ClientLogLevel): boolean => {
  if (appConfig.bootDiagnosticsEnabled) {
    return true;
  }
  return level === 'error';
};

export const trackRumAction = (name: string, attributes?: Record<string, unknown>) => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (Platform.OS === 'web') {
    try {
      const browserRum = getBrowserRum();
      browserRum?.addAction?.(name, attributes);
    } catch (error) {
      defaultConsole.warn('[datadog] Failed to track browser RUM action', error);
    }
    return;
  }

  const rumModule = getRumModule();
  if (!rumModule?.DdRum?.addAction) {
    return;
  }
  const actionType = rumModule.RumActionType?.CUSTOM ?? 'custom';
  rumModule.DdRum.addAction(actionType, name, attributes);
};

export const reportRumError = (error: unknown, attributes?: Record<string, unknown>) => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (Platform.OS === 'web') {
    try {
      const browserRum = getBrowserRum();
      if (!browserRum?.addError) {
        return;
      }
      browserRum.addError(asError(error), attributes);
    } catch (rumError) {
      defaultConsole.warn('[datadog] Failed to report browser RUM error', rumError);
    }
    return;
  }

  const rumModule = getRumModule();
  if (!rumModule?.DdRum?.addError) {
    return;
  }

  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? (error.stack ?? '') : '';
  const source = rumModule.ErrorSource?.SOURCE ?? 'SOURCE';

  rumModule.DdRum.addError(message, source, String(stack), attributes);
};

function serializeLogValue(value: unknown, depth = 0): unknown {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (depth >= 2 || value == null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => serializeLogValue(item, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .slice(0, 30)
      .map(([key, entry]) => [key, serializeLogValue(entry, depth + 1)]),
  );
}

function serializeLogAttributes(
  attributes?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!attributes) {
    return undefined;
  }

  return serializeLogValue(attributes) as Record<string, unknown>;
}

export const trackClientLog = (
  level: ClientLogLevel,
  message: string,
  attributes?: Record<string, unknown>,
) => {
  if (!appConfig.datadogEnabled) {
    return;
  }

  attributes = serializeLogAttributes(attributes);
  if (Platform.OS === 'web') {
    try {
      const browserLogs = getBrowserLogs();
      const logger = browserLogs?.logger;
      if (!logger) {
        return;
      }
      const context = attributes ? { ...attributes } : undefined;
      switch (level) {
        case 'debug':
          logger.debug?.(message, context);
          return;
        case 'warn':
          logger.warn?.(message, context);
          return;
        case 'error':
          logger.error?.(message, context);
          return;
        default:
          logger.info?.(message, context);
      }
    } catch (error) {
      defaultConsole.warn('[datadog] Failed to track browser client log', error);
    }
    return;
  }

  const logsModule = getNativeLogsModule();
  const logger = logsModule?.DdLogs;
  if (!logger || !nativeLogsReady) {
    return;
  }

  const context = attributes ? { ...attributes } : undefined;
  switch (level) {
    case 'debug':
      logger.debug?.(message, context);
      return;
    case 'warn':
      logger.warn?.(message, context);
      return;
    case 'error':
      logger.error?.(message, context);
      return;
    default:
      logger.info?.(message, context);
  }
};

export const clientLogger = {
  debug: (message: string, attributes?: Record<string, unknown>) =>
    trackClientLog('debug', message, attributes),
  info: (message: string, attributes?: Record<string, unknown>) =>
    trackClientLog('info', message, attributes),
  warn: (message: string, attributes?: Record<string, unknown>) =>
    trackClientLog('warn', message, attributes),
  error: (message: string, attributes?: Record<string, unknown>) =>
    trackClientLog('error', message, attributes),
};

export const setNativeLogsReady = (ready: boolean) => {
  nativeLogsReady = ready;
};

export const installClientConsoleForwarding = () => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (clientConsoleForwardingInstalled) {
    return;
  }

  const createForwarder =
    (method: 'log' | 'warn' | 'error') =>
    (...args: unknown[]) => {
      defaultConsole[method](...args);
      if (emittingClientLog) {
        return;
      }
      try {
        emittingClientLog = true;
        const level: ClientLogLevel = method === 'log' ? 'info' : method;
        if (!shouldForwardConsoleLevel(level)) {
          return;
        }
        const message = logMessageFromArgs(args);
        const firstArg = args.length > 0 ? sanitizeLogValue(args[0]) : undefined;
        const hasErrorArg = args.some((arg) => arg instanceof Error);
        clientLogger[level](message, {
          argCount: args.length,
          hasErrorArg,
          ...(appConfig.bootDiagnosticsEnabled
            ? { firstArg, details: logContextFromArgs(args)?.details }
            : {}),
          source: 'console',
        });
      } finally {
        emittingClientLog = false;
      }
    };

  console.log = createForwarder('log');
  console.warn = createForwarder('warn');
  console.error = createForwarder('error');
  clientConsoleForwardingInstalled = true;
};

export const startNativeRumView = (key: string, name: string, attributes?: Record<string, unknown>) => {
  const rumModule = getRumModule();
  void rumModule?.DdRum?.startView?.(key, name, attributes);
};

export const stopNativeRumView = (key: string, attributes?: Record<string, unknown>) => {
  const rumModule = getRumModule();
  void rumModule?.DdRum?.stopView?.(key, attributes);
};

export const installGlobalErrorHandler = () => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (Platform.OS === 'web') {
    return;
  }

  const errorUtils = (globalThis as { ErrorUtils?: any }).ErrorUtils;
  if (!errorUtils?.setGlobalHandler) {
    return;
  }

  const previousHandler = errorUtils.getGlobalHandler?.();
  errorUtils.setGlobalHandler((error: unknown, isFatal?: boolean) => {
    reportRumError(error, { isFatal: Boolean(isFatal) });
    if (typeof previousHandler === 'function') {
      previousHandler(error, isFatal);
    }
  });
};

export const maybeNativeRumConfig = (): DatadogProviderConfiguration | undefined => {
  if (!appConfig.datadogEnabled) {
    return undefined;
  }
  if (Platform.OS === 'web') {
    return undefined;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const rumModule = require('expo-datadog') as RumModule;
    const BatchSize = rumModule.BatchSize as { SMALL: unknown } | undefined;
    const UploadFrequency = rumModule.UploadFrequency as { FREQUENT: unknown } | undefined;
    const SdkVerbosity = rumModule.SdkVerbosity as { DEBUG: unknown } | undefined;
    const ProviderConfiguration = rumModule.DatadogProviderConfiguration;

    if (!ProviderConfiguration) {
      return undefined;
    }

    const nativeConfig = new ProviderConfiguration(
      'pub4cfde9641ec583f8075b638a3cb4c91c',
      appConfig.datadogEnv,
      'b8d783d6-63dc-4c30-a852-de31de3f8395',
      true,
      true,
      true,
    );

    nativeConfig.site = 'US5';
    nativeConfig.serviceName = Platform.select({
      android: 'pckm-mobile-android',
      default: 'pckm-mobile-ios',
      ios: 'pckm-mobile-ios',
    });
    nativeConfig.longTaskThresholdMs = 100;
    nativeConfig.nativeCrashReportEnabled = true;
    nativeConfig.sessionSamplingRate = 100;
    nativeConfig.resourceTracingSamplingRate = 20;
    nativeConfig.firstPartyHosts = [appConfig.apiUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '')];
    if (UploadFrequency?.FREQUENT) {
      nativeConfig.uploadFrequency = UploadFrequency.FREQUENT as never;
    }
    if (BatchSize?.SMALL) {
      nativeConfig.batchSize = BatchSize.SMALL as never;
    }
    if (SdkVerbosity?.DEBUG) {
      nativeConfig.verbosity = SdkVerbosity.DEBUG as never;
    }

    return nativeConfig;
  } catch (error) {
    console.error('[datadog] Failed to initialize native RUM config', error);
    return undefined;
  }
};

export const maybeInitBrowserLogs = () => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (Platform.OS !== 'web') {
    return;
  }
  if (browserLogsInitialized) {
    return;
  }

  try {
    const datadogLogs = getBrowserLogs();
    if (!datadogLogs?.init) {
      return;
    }
    datadogLogs.init({
      clientToken: 'pub2932b89bc1eaf577da16f15c97a2bd17',
      site: 'us5.datadoghq.com',
      service: 'pckm-web-client',
      env: appConfig.datadogEnv,
      sessionSampleRate: 100,
      forwardErrorsToLogs: false,
      forwardConsoleLogs: [],
    });
    browserLogsInitialized = true;
  } catch (error) {
    defaultConsole.warn('[datadog] Failed to initialize browser logs', error);
  }
};

export const maybeInitBrowserRum = () => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (Platform.OS !== 'web') {
    return;
  }
  if (browserRumInitialized) {
    return;
  }

  try {
    const datadogRum = getBrowserRum();
    if (!datadogRum?.init) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { reactPlugin } = require('@datadog/browser-rum-react');
    datadogRum.init({
      applicationId: '917fb993-250d-422c-98fe-ba6df950f87d',
      clientToken: 'pub2932b89bc1eaf577da16f15c97a2bd17',
      site: 'us5.datadoghq.com',
      service: 'pckm-web-client',
      env: appConfig.datadogEnv,
      trackViewsManually: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'allow',
      plugins: [reactPlugin()],
    });
    browserRumInitialized = true;
  } catch (error) {
    defaultConsole.warn('[datadog] Failed to initialize browser RUM', error);
  }
};

export const startBrowserRumView = (name: string, attributes?: Record<string, unknown>) => {
  if (!appConfig.datadogEnabled) {
    return;
  }
  if (Platform.OS !== 'web') {
    return;
  }

  try {
    const datadogRum = getBrowserRum();
    datadogRum?.startView?.({
      name,
      ...(attributes ? { context: attributes } : {}),
    });
  } catch (error) {
    defaultConsole.warn('[datadog] Failed to start browser view', error);
  }
};

export const normalizeRumViewName = (pathname: string): string => {
  if (pathname === '/' || pathname.length === 0) {
    return 'landing';
  }
  if (pathname === '/auth') {
    return 'auth';
  }
  if (pathname === '/create-league') {
    return 'create_league';
  }
  if (pathname === '/profile') {
    return 'profile';
  }
  if (pathname.startsWith('/join')) {
    return 'join_league';
  }
  if (pathname === '/admin/week-management') {
    return 'admin.week_management';
  }
  if (pathname === '/admin/bets-creator') {
    return 'admin.bets_creator';
  }
  if (pathname === '/admin/feedback') {
    return 'admin.feedback';
  }

  const leagueMatch = pathname.match(/^\/league\/([^/]+)(?:\/([^/]+))?$/);
  if (leagueMatch) {
    const section = leagueMatch[2];
    switch (section) {
      case 'draft':
        return 'league.draft';
      case 'picks':
        return 'league.picks';
      case 'standings':
        return 'league.standings';
      case 'rosters':
        return 'league.rosters';
      case 'scores':
        return 'league.scores';
      case 'leaderboard':
        return 'league.leaderboard';
      case 'stats':
        return 'league.stats';
      case 'payments':
        return 'league.payments';
      case 'admin':
        return 'league.admin';
      case 'squares':
        return 'league.squares';
      case 'settings':
        return 'league.settings';
      case 'season-editor':
        return 'league.season_editor';
      default:
        return 'league.index';
    }
  }

  return pathname
    .replace(/^\//, '')
    .replace(/\//g, '.')
    .replace(/[^a-zA-Z0-9._-]/g, '_');
};
